import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faCheckCircle,
  faDownload,
  faFileUpload,
  faFilter,
  faSpinner,
  faTimesCircle,
  faUsers,
  faWandMagicSparkles,
  faRobot,
  faXmark,
  faHistory,
} from "@fortawesome/free-solid-svg-icons";
import {
  exportDprValidationDashboardCsv,
  getDprValidationDashboardSummary,
  getDprValidationProcessedAiSummary,
  getDprValidationUploaders,
} from "../../services/dprValidationUploadApi";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const formatNumber = (value) => Number(value || 0).toLocaleString("en-US");

const formatDateTime = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString();
};

const toYmd = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const DprValidationDashboardPage = () => {
  const navigate = useNavigate();
  const today = useMemo(() => toYmd(new Date()), []);
  const yesterday = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return toYmd(d);
  }, []);
  const last7 = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return toYmd(d);
  }, []);

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [uploaders, setUploaders] = useState([]);
  const [uploadedBy, setUploadedBy] = useState("");
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);

  const [summary, setSummary] = useState({
    total_batches: 0,
    total_rows: 0,
    total_success: 0,
    total_failed: 0,
    total_uploaders: 0,
  });
  const [byUser, setByUser] = useState([]);
  const [daily, setDaily] = useState([]);

  const [errorMessage, setErrorMessage] = useState("");
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [aiSummaryFilterKey, setAiSummaryFilterKey] = useState("");
  const [hoveredSummaryCard, setHoveredSummaryCard] = useState(null);

  const aiSummaryFilters = useMemo(
    () => ({
      date_from: dateFrom || "",
      date_to: dateTo || "",
      validated_by_v2: uploadedBy || "",
    }),
    [dateFrom, dateTo, uploadedBy]
  );
  const aiSummaryCurrentFilterKey = useMemo(
    () => JSON.stringify(aiSummaryFilters),
    [aiSummaryFilters]
  );
  const hasAtLeastOneAiFilter = useMemo(
    () =>
      Object.values(aiSummaryFilters).some(
        (value) => String(value || "").trim() !== ""
      ),
    [aiSummaryFilters]
  );

  const loadUploaders = async () => {
    try {
      const response = await getDprValidationUploaders();
      setUploaders(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch {
      setUploaders([]);
    }
  };

  const fetchDashboard = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await getDprValidationDashboardSummary({
        date_from: dateFrom,
        date_to: dateTo,
        uploaded_by: uploadedBy,
      });

      const payload = response?.data || {};
      setSummary(payload.summary || {});
      setByUser(Array.isArray(payload.by_user) ? payload.by_user : []);
      setDaily(Array.isArray(payload.daily) ? payload.daily : []);
    } catch (error) {
      setSummary({
        total_batches: 0,
        total_rows: 0,
        total_success: 0,
        total_failed: 0,
        total_uploaders: 0,
      });
      setByUser([]);
      setDaily([]);
      setErrorMessage(
        error?.response?.data?.message ||
          "Unable to load dashboard data right now."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUploaders();
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const applyQuickRange = (type) => {
    if (type === "today") {
      setDateFrom(today);
      setDateTo(today);
      return;
    }
    if (type === "yesterday") {
      setDateFrom(yesterday);
      setDateTo(yesterday);
      return;
    }
    if (type === "last7") {
      setDateFrom(last7);
      setDateTo(today);
    }
  };

  const handleApplyFilters = () => {
    fetchDashboard();
  };

  const handleClearFilters = () => {
    setUploadedBy("");
    setDateFrom(today);
    setDateTo(today);
    setTimeout(() => {
      fetchDashboard();
    }, 0);
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const response = await exportDprValidationDashboardCsv({
        date_from: dateFrom,
        date_to: dateTo,
        uploaded_by: uploadedBy,
      });

      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute("download", `dpr_validation_dashboard_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // no-op
    } finally {
      setExporting(false);
    }
  };

  const handleOpenAiSummary = async (forceRegenerate = false) => {
    if (!hasAtLeastOneAiFilter) {
      toast.error("Apply at least one dashboard filter first before generating AI Summary.");
      return;
    }

    setAiModalOpen(true);
    setAiError("");

    if (!forceRegenerate && aiSummary && aiSummaryFilterKey === aiSummaryCurrentFilterKey) {
      return;
    }

    setAiLoading(true);
    try {
      const response = await getDprValidationProcessedAiSummary({
        ...aiSummaryFilters,
        ...(forceRegenerate ? { regenerate: 1 } : {}),
      });

      setAiSummary({
        ...(response?.data?.data || {}),
        source: response?.data?.source || "generated",
      });
      setAiSummaryFilterKey(aiSummaryCurrentFilterKey);
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to generate AI summary.";
      setAiError(message);
      toast.error(message);
    } finally {
      setAiLoading(false);
    }
  };

  const renderMarkdownAsHtml = (text) =>
    String(text || "")
      .replace(/^### (.*$)/gim, "<h3>$1</h3>")
      .replace(/^## (.*$)/gim, "<h2>$1</h2>")
      .replace(/^# (.*$)/gim, "<h1>$1</h1>")
      .replace(/\*\*(.*?)\*\*/gim, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/gim, "<em>$1</em>")
      .replace(/^- (.*$)/gim, "<li>$1</li>")
      .replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>")
      .replace(/\n{2,}/g, "</p><p>")
      .replace(/\n/g, "<br/>");

  // Modernized Card Data with better icons (matched with /dashboard style)
  const summaryCardData = [
    {
      icon: faFileUpload,
      title: "Total Batches",
      value: formatNumber(summary.total_batches),
      description: "Files uploaded",
      gradient: "from-sky-500 to-cyan-500",
      hoverGradient: "from-sky-600 to-cyan-600",
    },
    {
      icon: faCalendarAlt,
      title: "Total Rows",
      value: formatNumber(summary.total_rows),
      description: "All processed lines",
      gradient: "from-indigo-500 to-blue-500",
      hoverGradient: "from-indigo-600 to-blue-600",
    },
    {
      icon: faCheckCircle,
      title: "Validated Success",
      value: formatNumber(summary.total_success),
      description: "Rows accepted",
      gradient: "from-emerald-500 to-teal-500",
      hoverGradient: "from-emerald-600 to-teal-600",
    },
    {
      icon: faTimesCircle,
      title: "Failed Rows",
      value: formatNumber(summary.total_failed),
      description: "Rows rejected",
      gradient: "from-rose-500 to-pink-500",
      hoverGradient: "from-rose-600 to-pink-600",
    },
    {
      icon: faUsers,
      title: "Active Uploaders",
      value: formatNumber(summary.total_uploaders),
      description: "Unique users",
      gradient: "from-amber-500 to-orange-500",
      hoverGradient: "from-amber-600 to-orange-600",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-sky-50 to-blue-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl space-y-6">

        <div className="rounded-2xl bg-gradient-to-r from-sky-700 via-cyan-600 to-emerald-500 p-6 text-white shadow-lg">
          <h1 className="text-2xl font-bold tracking-tight">
            (DPR) Validation Dashboard
          </h1>
          <p className="mt-1 text-sm text-cyan-50">
            Validation activity by uploader with date filtering and CSV export.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-white/20 px-3 py-1">Queue-ready metrics</span>
            <span className="rounded-full bg-white/20 px-3 py-1">Per-user validated counts</span>
            <span className="rounded-full bg-white/20 px-3 py-1">Date-range analytics</span>
          </div>
        </div>

        <div className="rounded-2xl border border-cyan-100 bg-white p-5 shadow-sm">
          <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <FontAwesomeIcon icon={faFilter} />
            Dashboard Filters
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100"
              />
            </div>
            <div className="lg:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Uploaded By (optional)</label>
              <select
                value={uploadedBy}
                onChange={(e) => setUploadedBy(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100"
              >
                <option value="">All Uploaders</option>
                {uploaders.map((item) => {
                  const fullName = (item.full_name || "").trim();
                  const label = fullName ? `${fullName} (${item.username})` : item.username;
                  return (
                    <option key={item.username} value={item.username}>{label}</option>
                  );
                })}
              </select>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => applyQuickRange("today")} className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200">Today</button>
              <button onClick={() => applyQuickRange("yesterday")} className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200">Yesterday</button>
              <button onClick={() => applyQuickRange("last7")} className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200">Last 7 Days</button>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleApplyFilters}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-700 disabled:opacity-50"
              >
                {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCalendarAlt} />}
                Apply Filters
              </button>
              <button
                onClick={handleClearFilters}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
              >
                Reset
              </button>
              <button
                onClick={() => navigate("/dpr-validation-upload-ai-summary-history")}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
              >
                <FontAwesomeIcon icon={faHistory} />
                AI Summary History
              </button>
              <button
                onClick={handleExportCsv}
                disabled={exporting}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {exporting ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faDownload} />}
                Export CSV
              </button>
              <button
                onClick={() => handleOpenAiSummary(false)}
                className="group relative px-5 py-2.5 rounded-xl font-semibold text-sm text-white
                  bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600
                  hover:from-purple-500 hover:via-indigo-500 hover:to-blue-500
                  shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40
                  transition-all duration-300 ease-out hover:scale-[1.03] active:scale-[0.98]
                  flex items-center gap-2.5 overflow-hidden"
              >
                <span
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent
                  translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 ease-in-out"
                />
                <span
                  className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100
                  transition-opacity duration-500 ring-2 ring-indigo-400/50 ring-offset-2 ring-offset-transparent"
                />
                <FontAwesomeIcon
                  icon={faWandMagicSparkles}
                  className="relative z-10 text-base group-hover:rotate-12 transition-transform duration-300"
                />
                <span className="relative z-10">AI Summary</span>
                <span className="relative z-10 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-300 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-400" />
                </span>
              </button>
            </div>
          </div>

          {errorMessage && (
            <div className="mt-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
              {errorMessage}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          {summaryCardData.map((card, index) => (
            <div
              key={card.title}
              className={`group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 backdrop-blur-sm border border-white/20 bg-gradient-to-br bg-[length:200%_200%] bg-left-top hover:bg-right-bottom ${
                hoveredSummaryCard === index ? card.hoverGradient : card.gradient
              }`}
              onMouseEnter={() => setHoveredSummaryCard(index)}
              onMouseLeave={() => setHoveredSummaryCard(null)}
            >
              <div className="absolute inset-0 opacity-10">
                <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full bg-white/30 blur-xl transition-transform duration-500 group-hover:scale-150" />
                <div className="absolute -bottom-4 -left-4 h-16 w-16 rounded-full bg-white/20 blur-lg transition-transform duration-500 delay-100 group-hover:scale-150" />
              </div>

              <div className="absolute left-0 top-0 bottom-0 flex w-20 items-center justify-center overflow-hidden">
                <FontAwesomeIcon
                  icon={card.icon}
                  className={`text-5xl text-white transition-all duration-500 ${
                    hoveredSummaryCard === index
                      ? "opacity-40 scale-125"
                      : "opacity-30 scale-100"
                  }`}
                />
              </div>

              <div className="relative z-10 p-5 pl-24">
                <p className="text-xs font-semibold uppercase tracking-wide text-white/90">
                  {card.title}
                </p>
                <p className="mt-2 text-2xl font-bold tracking-tight text-white">
                  {card.value}
                </p>
                <p className="mt-1 text-xs font-medium text-white/70 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  {card.description}
                </p>
              </div>

              <div className="absolute bottom-4 right-4 translate-x-2 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20">
                  <div className="-translate-x-0.5 -translate-y-0.5 h-2 w-2 rotate-45 border-r-2 border-b-2 border-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          {/* Doughnut: overall success vs failed */}
          <div className="rounded-2xl border border-cyan-100 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-700">Success vs Failed</h2>
            <p className="mb-4 text-xs text-gray-400">Overall ratio for selected period.</p>
            {(Number(summary.total_success) > 0 || Number(summary.total_failed) > 0) ? (
              <div className="flex justify-center" style={{ maxHeight: 220 }}>
                <Doughnut
                  data={{
                    labels: ["Success", "Failed"],
                    datasets: [{
                      data: [Number(summary.total_success), Number(summary.total_failed)],
                      backgroundColor: ["#10b981", "#ef4444"],
                      borderWidth: 0,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: { position: "bottom", labels: { font: { size: 11 } } },
                    },
                  }}
                />
              </div>
            ) : (
              <p className="py-14 text-center text-xs text-gray-400">No data</p>
            )}
          </div>

          {/* Bar: daily success / failed trend */}
          <div className="rounded-2xl border border-cyan-100 bg-white p-5 shadow-sm xl:col-span-2">
            <h2 className="text-sm font-semibold text-slate-700">Daily Trend</h2>
            <p className="mb-4 text-xs text-gray-400">Success and failed rows by upload date.</p>
            {daily.length > 0 ? (
              <Bar
                data={{
                  labels: daily.map((d) => d.date_label),
                  datasets: [
                    {
                      label: "Success",
                      data: daily.map((d) => Number(d.total_success)),
                      backgroundColor: "#10b981",
                      borderRadius: 4,
                    },
                    {
                      label: "Failed",
                      data: daily.map((d) => Number(d.total_failed)),
                      backgroundColor: "#ef4444",
                      borderRadius: 4,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { position: "bottom", labels: { font: { size: 11 } } },
                  },
                  scales: {
                    x: { ticks: { font: { size: 10 } } },
                    y: { beginAtZero: true, ticks: { font: { size: 10 } } },
                  },
                }}
              />
            ) : (
              <p className="py-14 text-center text-xs text-gray-400">No daily data in this range.</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <div className="rounded-2xl border border-cyan-100 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-cyan-50 px-4 py-3">
              <h2 className="text-sm font-semibold text-slate-700">Per User Validation Totals</h2>
              <p className="text-xs text-gray-400">Who validated how many rows in the selected period.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left">Uploader</th>
                    <th className="px-4 py-3 text-right">Batches</th>
                    <th className="px-4 py-3 text-right">Rows</th>
                    <th className="px-4 py-3 text-right">Success</th>
                    <th className="px-4 py-3 text-right">Failed</th>
                    <th className="px-4 py-3 text-left">Last Upload</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {byUser.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-gray-400">No records in this range.</td>
                    </tr>
                  ) : (
                    byUser.map((row, idx) => (
                      <tr key={`${row.uploaded_by}-${idx}`} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-700">{row.uploader_full_name || row.uploaded_by}</p>
                          <p className="text-xs text-gray-400">{row.uploaded_by}</p>
                        </td>
                        <td className="px-4 py-3 text-right text-slate-600">{formatNumber(row.batches)}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{formatNumber(row.total_rows)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-emerald-700">{formatNumber(row.total_success)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-red-600">{formatNumber(row.total_failed)}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{formatDateTime(row.last_upload_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-2xl border border-cyan-100 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-cyan-50 px-4 py-3">
              <h2 className="text-sm font-semibold text-slate-700">Daily Validation Activity</h2>
              <p className="text-xs text-gray-400">Trend view by upload date.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left">Date</th>
                    <th className="px-4 py-3 text-right">Batches</th>
                    <th className="px-4 py-3 text-right">Success</th>
                    <th className="px-4 py-3 text-right">Failed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {daily.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-10 text-center text-gray-400">No daily trend data in this range.</td>
                    </tr>
                  ) : (
                    daily.map((row, idx) => (
                      <tr key={`${row.date_label}-${idx}`} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-slate-700">{row.date_label}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{formatNumber(row.batches)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-emerald-700">{formatNumber(row.total_success)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-red-600">{formatNumber(row.total_failed)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="rounded-xl border border-cyan-100 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500">
              <FontAwesomeIcon icon={faUsers} />
              Uploaders
            </div>
            <p className="mt-2 text-sm text-gray-600">Filter dashboard by a specific uploader or keep it global for all users.</p>
          </div>
          <div className="rounded-xl border border-cyan-100 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500">
              <FontAwesomeIcon icon={faFileUpload} />
              Validation Volume
            </div>
            <p className="mt-2 text-sm text-gray-600">Total rows and success/failure counts come from processed queue results.</p>
          </div>
          <div className="rounded-xl border border-cyan-100 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500">
              <FontAwesomeIcon icon={faCheckCircle} />
              Quick Report
            </div>
            <p className="mt-2 text-sm text-gray-600">Use Export CSV to generate date-filtered dashboard records for reporting.</p>
          </div>
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* AI Summary Modal */}
      {aiModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={() => setAiModalOpen(false)}
          />
          <div
            className="relative w-full max-w-3xl max-h-[85vh] bg-white rounded-2xl shadow-2xl
            animate-slide-up flex flex-col overflow-hidden border border-gray-200"
          >
            <div
              className="flex items-center justify-between px-6 py-4 border-b border-gray-200
              bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <FontAwesomeIcon icon={faRobot} className="text-white text-lg" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">DPR Dashboard AI Summary</h2>
                  <p className="text-xs text-indigo-200">
                    {aiSummary?.model_used ? `Model: ${aiSummary.model_used}` : "Powered by AI"}
                    {aiSummary?.source === "cached" && " - Cached"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setAiModalOpen(false)}
                className="p-2 rounded-lg hover:bg-white/20 text-white transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faXmark} className="text-lg" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {aiLoading && (
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full border-4 border-indigo-100 border-t-indigo-600 animate-spin" />
                    <FontAwesomeIcon
                      icon={faWandMagicSparkles}
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-indigo-600 text-xl animate-pulse"
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-700">Generating AI Summary...</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Analyzing dashboard filter scope. This may take a few seconds.
                    </p>
                  </div>
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}

              {aiError && !aiLoading && (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <p className="text-sm text-red-600 font-medium">{aiError}</p>
                  <button
                    onClick={() => handleOpenAiSummary(true)}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg transition-colors duration-200"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {aiSummary && !aiLoading && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2">
                      <p className="text-xs text-gray-500">Filtered Rows</p>
                      <p className="text-sm font-bold text-indigo-700">
                        {(aiSummary.filtered_total_rows || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2">
                      <p className="text-xs text-gray-500">Success</p>
                      <p className="text-sm font-bold text-emerald-700">
                        {(aiSummary.filtered_success_count || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2">
                      <p className="text-xs text-gray-500">Failed</p>
                      <p className="text-sm font-bold text-red-700">
                        {(aiSummary.filtered_failed_count || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
                      <p className="text-xs text-gray-500">Below Quota</p>
                      <p className="text-sm font-bold text-amber-700">
                        {(aiSummary.below_quota_count || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div
                    className="prose prose-sm max-w-none prose-headings:text-gray-800 prose-headings:font-bold
                    prose-p:text-gray-700 prose-strong:text-gray-800 prose-ul:text-gray-700 prose-li:text-gray-700
                    prose-h2:text-lg prose-h2:mt-5 prose-h2:mb-2 prose-h3:text-base prose-h3:mt-4 prose-h3:mb-1"
                    dangerouslySetInnerHTML={{
                      __html: renderMarkdownAsHtml(aiSummary.summary_text || ""),
                    }}
                  />
                </div>
              )}
            </div>

            {aiSummary && !aiLoading && (
              <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
                <div className="text-xs text-gray-500 flex flex-wrap gap-4">
                  <span>
                    Quota target/day: <strong>{aiSummary.quota_target || 272}</strong>
                  </span>
                  {aiSummary.prompt_tokens != null && (
                    <span>
                      Prompt tokens: <strong>{aiSummary.prompt_tokens?.toLocaleString()}</strong>
                    </span>
                  )}
                  {aiSummary.completion_tokens != null && (
                    <span>
                      Completion tokens: <strong>{aiSummary.completion_tokens?.toLocaleString()}</strong>
                    </span>
                  )}
                  {aiSummary.total_tokens != null && (
                    <span>
                      Total tokens: <strong>{aiSummary.total_tokens?.toLocaleString()}</strong>
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleOpenAiSummary(true)}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg transition-colors duration-200 font-medium"
                  >
                    Regenerate
                  </button>
                  <button
                    onClick={() => setAiModalOpen(false)}
                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm rounded-lg transition-colors duration-200 font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DprValidationDashboardPage;

